import { getMindboxDeviceUuid } from '@/shared/lib/mindboxDeviceUuid';
import {
  MINDBOX_DEVICE_UUID_POLL_MS,
  MINDBOX_DEVICE_UUID_WAIT_MS,
  MINDBOX_ENDPOINT_ID,
  MINDBOX_OPERATIONS_URL,
} from '../config';
import { MindboxOperation, type MindboxCustomFields } from '../model/mindbox';

function waitMindboxDeviceUuid(): Promise<string | null> {
  const deadline = Date.now() + MINDBOX_DEVICE_UUID_WAIT_MS;

  return new Promise((resolve) => {
    const check = (): void => {
      const deviceUuid = getMindboxDeviceUuid();

      if (deviceUuid || Date.now() >= deadline) {
        resolve(deviceUuid || null);
        return;
      }

      setTimeout(check, MINDBOX_DEVICE_UUID_POLL_MS);
    };

    check();
  });
}

function getCategoryId({ countryId }: MindboxCustomFields): string | number | undefined {
  return Array.isArray(countryId) ? countryId[0] : undefined;
}

export default class Mindbox {
  private deviceUuid: string | null = null;

  private deviceUuidPromise: Promise<string | null> | null = null;

  viewCategory(customFields: MindboxCustomFields): Promise<boolean> {
    return this.operation(MindboxOperation.ViewCategory, {
      viewProductCategory: {
        productCategory: { ids: { hotels: getCategoryId(customFields) } },
        customerAction: { customFields },
      },
    });
  }

  private operation(operation: MindboxOperation, data: object): Promise<boolean> {
    if (typeof window === 'undefined' || typeof fetch !== 'function') return Promise.resolve(false);

    return this.send(operation, data).catch(() => false);
  }

  private async send(operation: MindboxOperation, data: object): Promise<boolean> {
    const body = JSON.stringify(data);
    const deviceUUID = await this.getDeviceUuid();

    if (!deviceUUID) return false;

    const query = new URLSearchParams({ endpointId: MINDBOX_ENDPOINT_ID, operation, deviceUUID });
    const response = await fetch(`${MINDBOX_OPERATIONS_URL}?${query.toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'omit',
      keepalive: true,
    });

    return response.ok;
  }

  private async getDeviceUuid(): Promise<string | null> {
    if (this.deviceUuid) return this.deviceUuid;

    this.deviceUuidPromise ??= waitMindboxDeviceUuid().finally(() => {
      this.deviceUuidPromise = null;
    });
    this.deviceUuid = await this.deviceUuidPromise;

    return this.deviceUuid;
  }
}

const mindbox = new Mindbox();

export { mindbox };
